import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Fade,
  Heading,
  Link,
  Menu,
  MenuItem,
  MenuOverlay,
  PlusIcon,
  SettingsIcon,
  Spinner,
  Text,
} from '@razorpay/blade/components';
import { EditIcon } from '@razorpay/blade/components';
import overtimeIllustration from 'assets/tna-settings/overtime.svg';

import type {
  OvertimeFormula,
  OvertimeFormulaFormData,
} from 'components/Settings/AttendanceAndLeavesSettings/types';
import { OvertimeFormulaModal } from './OvertimeFormulaModal';
import { EditSalaryComponentsModal } from './EditSalaryComponentsModal';
import {
  useOvertimeFormulaCards,
  useCreateFormulaCard,
  useOtSalaryComponents,
  useBulkUpdateOtBehaviour,
} from './hooks';
import { routePaths } from 'components/Routes/data';
import {
  buildFormulaDescription,
  computeOtComponentChanges,
} from 'components/Settings/AttendanceAndLeavesSettings/utils';
import { DisableFormulaModal } from './DisableFormulaModal';
import { FORMULA_RATE_TYPE } from 'components/Settings/AttendanceAndLeavesSettings/constants';
import { useFormWithProvider } from 'forms/useFormWithProvider';
import { UbitechOvertimeCards } from './UbitechOvertimeCards';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';

export const OvertimeSection = () => {
  const currentOrganization = useReduxSelector(loggedInUserSelectors.currentOrganization);
  const isUbitechOrg = !!currentOrganization?.isUbitechOrg;
  const { data: formulas = [], isLoading, isError, refetch } = useOvertimeFormulaCards();
  const { mutate: createFormula, isLoading: isCreating } = useCreateFormulaCard();
  const { setFieldValue } = useFormWithProvider<OvertimeFormulaFormData>();
  const {
    data: otComponents = [],
    refetch: refetchOtComponents,
    isRefetching: isRefetchingComponents,
  } = useOtSalaryComponents({
    onSuccess: (data) => {
      setFieldValue(
        'selectedComponentIds',
        data.filter((c) => c.includeInOtWages).map((c) => c.id),
      );
    },
  });
  const { mutate: bulkUpdateComponents } = useBulkUpdateOtBehaviour();

  const [formulaModalOpen, setFormulaModalOpen] = useState(false);
  const [editComponentsOpen, setEditComponentsOpen] = useState(false);
  const [formulaToDisable, setFormulaToDisable] = useState<OvertimeFormula | null>(null);

  const openFormulaModal = async () => {
    await refetchOtComponents();
    setFormulaModalOpen(true);
  };

  const hasFormulas = formulas.length > 0;

  const handleFormulaSave = (formula: OvertimeFormula, selectedComponentIds?: string[]) => {
    if (selectedComponentIds !== undefined) {
      const changes = computeOtComponentChanges(otComponents, selectedComponentIds);
      if (changes.length > 0) {
        bulkUpdateComponents(changes);
      }
    }
    createFormula(formula, {
      onSuccess: () => {
        setFormulaModalOpen(false);
      },
    });
  };

  const includedComponents = otComponents.filter((c) => c.includeInOtWages);
  const componentDisplayNames = includedComponents.map((c) => c.name).join(', ');

  if (isLoading) {
    return (
      <Box
        padding="spacing.6"
        borderRadius="medium"
        borderWidth="thin"
        borderColor="surface.border.gray.subtle"
        backgroundColor="surface.background.gray.intense">
        <Heading size="medium">Overtime</Heading>
        <Box
          marginTop="spacing.6"
          display="flex"
          justifyContent="center"
          alignItems="center"
          paddingY="spacing.10">
          <Spinner accessibilityLabel="Loading overtime formulas" />
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        padding="spacing.6"
        borderRadius="medium"
        borderWidth="thin"
        borderColor="surface.border.gray.subtle"
        backgroundColor="surface.background.gray.intense">
        <Heading size="medium">Overtime</Heading>
        <Box
          marginTop="spacing.6"
          paddingY="spacing.7"
          paddingX="spacing.6"
          borderRadius="medium"
          borderWidth="thin"
          borderColor="surface.border.gray.subtle"
          backgroundColor="surface.background.gray.moderate"
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="spacing.4">
          <Text color="feedback.text.negative.intense">Failed to load overtime formulas.</Text>
          <Button variant="secondary" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (!hasFormulas && !isUbitechOrg) {
    return (
      <Fade motionTriggers={['mount']}>
        <Box
          padding="spacing.6"
          borderRadius="medium"
          borderWidth="thin"
          borderColor="surface.border.gray.subtle"
          backgroundColor="surface.background.gray.intense">
          <Heading size="medium">Overtime</Heading>
          <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
            Define how overtime gets paid. Apply rates automatically each payroll cycle, no manual
            calculation.
          </Text>

          <Box
            marginTop="spacing.6"
            paddingY="spacing.7"
            paddingX="spacing.6"
            borderRadius="medium"
            borderWidth="thin"
            borderColor="surface.border.gray.subtle"
            backgroundColor="surface.background.gray.moderate"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="spacing.6">
            <img src={overtimeIllustration} alt="Overtime illustration" width={150} height={124} />
            <Box display="flex" flexDirection="column" alignItems="center" gap="spacing.2">
              <Text weight="semibold">No overtime rates configured yet</Text>
              <Text size="medium" color="surface.text.gray.muted" textAlign="center">
                Set up rates for weekday, weekend, or holiday work.
              </Text>
            </Box>
            <Button
              variant="secondary"
              icon={PlusIcon}
              iconPosition="right"
              isLoading={isRefetchingComponents}
              onClick={openFormulaModal}>
              Set Overtime Rates
            </Button>
          </Box>

          <OvertimeFormulaModal
            isOpen={formulaModalOpen}
            onDismiss={() => setFormulaModalOpen(false)}
            onSave={handleFormulaSave}
            otComponents={otComponents}
            isSubmitting={isCreating}
          />
        </Box>
      </Fade>
    );
  }

  const activeMultiplierCount = formulas.filter(
    (f) => f.isActive && f.rateType === FORMULA_RATE_TYPE.multiplier,
  ).length;

  return (
    <Box
      padding="spacing.6"
      borderRadius="medium"
      borderWidth="thin"
      borderColor="surface.border.gray.subtle"
      backgroundColor="surface.background.gray.intense">
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Heading size="medium">Overtime Policy</Heading>
          <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
            Configure overtime formulas for payroll calculations.
          </Text>
        </Box>
        {!isUbitechOrg && (
          <Button
            variant="secondary"
            size="small"
            icon={PlusIcon}
            iconPosition="left"
            isLoading={isRefetchingComponents}
            onClick={openFormulaModal}>
            Create Formula
          </Button>
        )}
      </Box>

      {isUbitechOrg ? (
        <UbitechOvertimeCards
          otComponents={otComponents}
          formulas={formulas}
          onBeforeOpenModal={async () => {
            await refetchOtComponents();
          }}
        />
      ) : (
        <Box marginTop="spacing.6" display="flex" flexDirection="column" gap="spacing.3">
          {formulas.map((formula) => (
            <Box
              key={formula.id}
              padding="spacing.5"
              borderRadius="medium"
              borderWidth="thin"
              borderColor="surface.border.gray.subtle"
              backgroundColor="surface.background.gray.moderate"
              display="flex"
              alignItems="center"
              gap="spacing.4">
              <Box flex="1">
                <Text weight="medium">{formula.name}</Text>
                <Text size="small" color="surface.text.gray.muted">
                  {buildFormulaDescription(formula, otComponents)}
                </Text>
              </Box>
              {formula.isActive ? (
                <Badge color="positive" size="small">
                  Active
                </Badge>
              ) : (
                <Badge color="neutral" size="small">
                  Disabled
                </Badge>
              )}

              {formula.isActive && (
                <Menu openInteraction="click">
                  <Button
                    variant="tertiary"
                    size="small"
                    icon={SettingsIcon}
                    accessibilityLabel="Formula actions"
                    onClick={() => undefined}
                  />
                  <MenuOverlay>
                    <MenuItem
                      title="Disable"
                      description="Permanently disable this formula from payroll calculations."
                      color="negative"
                      onClick={() => setFormulaToDisable(formula)}
                    />
                  </MenuOverlay>
                </Menu>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Salary components metadata row */}
      <Box marginTop="spacing.6">
        <Divider />
        <Box marginTop="spacing.4" display="flex" alignItems="center" gap="spacing.3">
          <Box flex="1">
            <Text size="medium" color="surface.text.gray.muted">
              <Text as="span" size="medium" weight="semibold" color="surface.text.gray.muted">
                Salary components:
              </Text>{' '}
              {componentDisplayNames || 'None selected'}
              {' · '}
              <Link
                href={routePaths.salaryComponents.earningsListView}
                target="_blank"
                size="medium">
                View in Component Library →
              </Link>
            </Text>
          </Box>
          <Button
            variant="tertiary"
            size="small"
            icon={EditIcon}
            accessibilityLabel="Edit salary components"
            onClick={() => setEditComponentsOpen(true)}
          />
        </Box>
      </Box>

      <OvertimeFormulaModal
        isOpen={formulaModalOpen}
        onDismiss={() => setFormulaModalOpen(false)}
        onSave={handleFormulaSave}
        otComponents={otComponents}
        isSubmitting={isCreating}
      />

      <EditSalaryComponentsModal
        isOpen={editComponentsOpen}
        onDismiss={() => setEditComponentsOpen(false)}
        activeMultiplierCount={activeMultiplierCount}
        onSuccess={() => setEditComponentsOpen(false)}
      />

      {formulaToDisable && (
        <DisableFormulaModal
          onDismiss={() => setFormulaToDisable(null)}
          formula={formulaToDisable}
        />
      )}
    </Box>
  );
};
